import { request } from "$/lib/api"
import {
  EmergencyServiceType,
  Route,
  SaveEmergencyServicePrice,
  ToggleEmergencyService,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

interface EmergencyServiceSettings {
  allowsEmergencyService: boolean
}

async function fetchEmergencyServiceSettings(
  storeId: string,
): Promise<EmergencyServiceSettings> {
  return await request<EmergencyServiceSettings>({
    url: populateRoute(Route.EMERGENCY_SERVICE__GET_SETTINGS, storeId),
    method: "GET",
  })
}

async function fetchEmergencyServicePrices(
  storeId: string,
): Promise<Record<EmergencyServiceType, number>> {
  return await request<Record<EmergencyServiceType, number>>({
    url: populateRoute(Route.EMERGENCY_SERVICE__FIND_ALL, storeId),
    method: "GET",
  })
}

async function saveEmergencyServicePrice(
  data: SaveEmergencyServicePrice,
  storeId: string,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.EMERGENCY_SERVICE__SAVE, storeId),
    method: "POST",
    data,
  })
}

async function toggleEmergencyService(
  data: ToggleEmergencyService,
  storeId: string,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.EMERGENCY_SERVICE__TOGGLE, storeId),
    method: "PATCH",
    data,
  })
}

export function useEmergencyServiceMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () => {
    await queryClient.invalidateQueries(
      emergencyServiceQueries.prices.list(storeId),
    )
    await queryClient.invalidateQueries(
      emergencyServiceQueries.settings.byStoreId(storeId),
    )
  }

  const saveMutation = useMutation({
    mutationFn: (data: SaveEmergencyServicePrice) =>
      saveEmergencyServicePrice(data, storeId),
    onSettled: invalidateQueries,
  })

  const toggleMutation = useMutation({
    mutationFn: (data: ToggleEmergencyService) =>
      toggleEmergencyService(data, storeId),
    onSettled: invalidateQueries,
  })

  return { saveMutation, toggleMutation }
}

const emergencyServiceQueries = {
  all: () => ["emergency-service"],
  settings: {
    all: () => [...emergencyServiceQueries.all(), "settings"],
    byStoreId: (storeId: string) =>
      queryOptions({
        queryKey: [...emergencyServiceQueries.settings.all(), storeId],
        queryFn: () => fetchEmergencyServiceSettings(storeId),
      }),
  },
  prices: {
    all: () => [...emergencyServiceQueries.all(), "prices"],
    lists: () => [...emergencyServiceQueries.prices.all(), "list"],
    list: (storeId: string) =>
      queryOptions({
        queryKey: [...emergencyServiceQueries.prices.lists(), storeId],
        queryFn: () => fetchEmergencyServicePrices(storeId),
      }),
  },
}

// Keep backward compatibility
const emergencyServicePricesQueries = emergencyServiceQueries.prices

export { emergencyServiceQueries }
export default emergencyServicePricesQueries
